import { LIST_OPERATORS, VALUELESS_OPERATORS, WORKFLOW_LIMITS, type ConditionOperator } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Input, Select, Textarea } from "@sagot/ui";
import { useContext } from "react";
import type { Condition, Conditions } from "../types";
import type { ServiceFieldOption } from "../service/service-refs";
import { choicesOf, fieldGroups, fieldKey, isListedField, kindOfField, operatorsFor, valueFor, type Definitions, type FieldGroup } from "./condition-fields";
import { HookKeysContext, RunVariablesContext, TriggerContext } from "./hook-model";
import { useRefs } from "./refs";

export { operatorsFor, valueFor } from "./condition-fields";

const C = "engage.workflows.conditions";
/** A new condition starts on the first fact of the picker (the channel of the conversation; a fact of the ticket on a ticket moment). */
const startingCondition = (groups: readonly FieldGroup[]): Condition => {
  const first = groups[0]?.fields[0]?.field ?? "conversation.channel";
  const priority = groups.some((group) => group.fields.some((item) => item.field === "ticket.priority"));
  return { field: priority ? "ticket.priority" : first, op: "is", value: "" };
};

function ValueInput({ condition, label, definitions, ticketFields, onChange }: { condition: Condition; label: string; definitions: Definitions; ticketFields: readonly ServiceFieldOption[]; onChange: (value: Condition["value"]) => void }) {
  const { t } = useTranslation();
  const kind = kindOfField(condition.field, definitions, ticketFields);
  if (VALUELESS_OPERATORS.has(condition.op)) return null;
  if (kind === "bool") {
    return (
      <Select aria-label={label} className="h-9 text-xs" value={condition.value === false ? "false" : "true"} onChange={(e) => { onChange(e.target.value === "true"); }}>
        <option value="true">{t(`${C}.yes`)}</option>
        <option value="false">{t(`${C}.no`)}</option>
      </Select>
    );
  }
  const choices = choicesOf(condition.field);
  if (choices && !LIST_OPERATORS.has(condition.op)) {
    const current = typeof condition.value === "string" ? condition.value : "";
    return (
      <Select aria-label={label} className="h-9 text-xs" value={current} onChange={(e) => { onChange(e.target.value); }}>
        {choices.includes(current) ? null : <option value="">{t(`${C}.choose`)}</option>}
        {choices.map((choice) => <option key={choice} value={choice}>{t(`${C}.choices.${fieldKey(condition.field)}.${choice}`)}</option>)}
      </Select>
    );
  }
  if (LIST_OPERATORS.has(condition.op)) {
    const lines = Array.isArray(condition.value) ? condition.value.join("\n") : "";
    return <Textarea aria-label={label} rows={2} className="text-xs" placeholder={t(`${C}.listHint`)} value={lines} onChange={(e) => { onChange(e.target.value.split("\n").slice(0, WORKFLOW_LIMITS.valuesPerCondition)); }} />;
  }
  return (
    <Input
      aria-label={label}
      className="h-9 text-xs"
      inputMode={kind === "number" ? "numeric" : undefined}
      value={condition.value === null || condition.value === undefined ? "" : String(condition.value)}
      onChange={(e) => { onChange(kind === "number" ? Number(e.target.value) || 0 : e.target.value); }}
    />
  );
}

function FieldOptions({ groups, current }: { groups: readonly FieldGroup[]; current: string }) {
  const { t } = useTranslation();
  return (
    <>
      {isListedField(current, groups) ? null : <option value={current}>{current}</option>}
      {groups.map((group) => (
        <optgroup key={`${group.key}-${group.attribute ?? "built-in"}`} label={t(group.attribute ? `${C}.attributeGroups.${group.attribute}` : `${C}.groups.${group.key}`)}>
          {group.fields.map((item) => <option key={item.field} value={item.field}>{item.label ?? t(`${C}.fields.${fieldKey(item.field)}`)}</option>)}
        </optgroup>
      ))}
    </>
  );
}

interface ConditionEditorProps {
  value: Conditions;
  disabled: boolean;
  onChange: (value: Conditions) => void;
}

/** OR groups of AND conditions; no groups = always. Custom attributes join the field list. */
export function ConditionEditor({ value, disabled, onChange }: ConditionEditorProps) {
  const { t } = useTranslation();
  const { attributes, service } = useRefs();
  const ticketFields = service?.fields ?? [];
  const groups = fieldGroups(attributes, useContext(HookKeysContext), useContext(TriggerContext), useContext(RunVariablesContext), ticketFields);
  const setCondition = (g: number, c: number, next: Condition | null) => {
    const kept = value.any
      .map((group, gi) => (gi !== g ? group : { all: group.all.flatMap((cond, ci) => (ci !== c ? [cond] : next ? [next] : [])) }))
      .filter((group) => group.all.length > 0);
    onChange({ any: kept });
  };
  const addTo = (g: number) => {
    onChange({ any: value.any.map((group, gi) => (gi === g ? { all: [...group.all, startingCondition(groups)] } : group)) });
  };
  return (
    <div className="space-y-3">
      {value.any.map((group, g) => (
        <div key={g} className="space-y-2">
          {g > 0 ? <p className="text-xs font-bold uppercase text-fg-subtle">{t(`${C}.or`)}</p> : null}
          <div className="space-y-2 rounded-md border border-line p-3">
            {group.all.map((condition, c) => {
              const label = `${t(`${C}.value`)} ${String(c + 1)}`;
              return (
                <div key={c} className="flex flex-wrap items-center gap-2">
                  {c > 0 ? <span className="text-xs font-bold text-fg-subtle">{t(`${C}.and`)}</span> : null}
                  <Select aria-label={t(`${C}.field`)} className="h-9 w-auto text-xs" disabled={disabled} value={condition.field} onChange={(e) => {
                    const field = e.target.value;
                    const operators = operatorsFor(field, attributes, ticketFields);
                    const op = operators.includes(condition.op) ? condition.op : (operators[0] ?? "is");
                    setCondition(g, c, { field, op, value: valueFor(field, op, condition.value, attributes, ticketFields) });
                  }}>
                    <FieldOptions groups={groups} current={condition.field} />
                  </Select>
                  <Select aria-label={t(`${C}.operator`)} className="h-9 w-auto text-xs" disabled={disabled} value={condition.op} onChange={(e) => {
                    const op = e.target.value as ConditionOperator;
                    setCondition(g, c, { ...condition, op, value: valueFor(condition.field, op, condition.value, attributes, ticketFields) });
                  }}>
                    {operatorsFor(condition.field, attributes, ticketFields).map((op) => <option key={op} value={op}>{t(`${C}.operators.${op}`)}</option>)}
                  </Select>
                  <div className="min-w-32 flex-1">
                    <ValueInput condition={condition} label={label} definitions={attributes} ticketFields={ticketFields} onChange={(next) => { setCondition(g, c, { ...condition, value: next }); }} />
                  </div>
                  <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled} aria-label={t(`${C}.remove`)} onClick={() => { setCondition(g, c, null); }}>
                    <Icon name="x" />
                  </Button>
                </div>
              );
            })}
            {group.all.length < WORKFLOW_LIMITS.conditionsPerGroup ? (
              <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => { addTo(g); }}>
                <Icon name="plus" />
                {t(`${C}.add`)}
              </Button>
            ) : null}
          </div>
        </div>
      ))}
      {value.any.length < WORKFLOW_LIMITS.conditionGroups ? (
        <Button type="button" variant="secondary" size="sm" disabled={disabled} onClick={() => { onChange({ any: [...value.any, { all: [startingCondition(groups)] }] }); }}>
          <Icon name="plus" />
          {value.any.length === 0 ? t(`${C}.add`) : t(`${C}.addGroup`)}
        </Button>
      ) : null}
    </div>
  );
}
